import React, { useState, useEffect } from 'react';
import '../../index.css';
import { XMarkIcon } from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return isMobile;
}

function Navbar({ SetActiveDocs, activeSection, setIsOpen }) {
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  const [admissionOpen, setAdmissionOpen] = useState(false);
  const navigate = useNavigate();

  const admissionSubLinks = [
    { text: 'ليه تدرس في امريكا', key: 'ليه تدرس في امريكا' },
    { text: 'الشروط الاساسية',    key: 'الشروط الاساسية' },
    { text: 'انواع الجامعات',     key: 'انواع الجامعات' },
    { text: 'الدعم المادي/المنح', key: 'الدعم المادي/المنح' },
    { text: 'Academics',          key: 'Academics' },
    { text: 'Extracurriculars',   key: 'Extracurriculars' },
    { text: 'انواع التقديم',      key: 'انواع التقديم' },
    { text: 'Common App',         key: 'Common App' },
  ];

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const closeMenu = () => {
    setMenuOpen(false);
    setAdmissionOpen(false);
  };

  // Navigate home and scroll to a section.
  // We pass the target section id via router state so LandingPage
  // can scroll after it has fully mounted — no timing race.
  const goHome = (sectionId) => {
    closeMenu();
    navigate('/', { state: { scrollTo: sectionId || 'hero' } });
  };

  const goDocs = (key) => {
    closeMenu();
    SetActiveDocs(key);
    navigate('/Docs');
  };

  const Logo = () => (
    <Link to="/" onClick={() => navigate('/', { state: { scrollTo: 'hero' } })}>
      <div className="logo text-right">
        <div style={{ color: '#E0E0E0' }}>Campus</div>
        <div style={{ color: '#3555C6' }}>Odyssey</div>
      </div>
    </Link>
  );

  const DesktopLink = ({ label, section, onClick }) => {
    const isActive = activeSection === section;
    return (
      <button
        onClick={onClick}
        className="relative pb-1 text-[15px] font-normal transition-colors duration-200 cursor-pointer bg-transparent border-none p-0"
      >
        <span className={isActive ? 'text-[#3E7CFF]' : 'text-[#A0A0A0] hover:text-[#E0E0E0]'}>
          {label}
        </span>
        {isActive && (
          <motion.span
            layoutId="underline"
            className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#3E7CFF] rounded-full"
          />
        )}
      </button>
    );
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-[100] h-[72px] bg-[#0D0D0D] border-b border-white/[0.06] text-white">
        <div className="xl:max-w-screen-xl md:max-w-screen-md mx-auto px-5 h-full flex items-center justify-between">

          {!isMobile ? (
            <div className="flex items-center gap-9">
              <DesktopLink label="مسابقات"  section="competitions" onClick={() => goDocs('programs')} />
              <DesktopLink label="بروجرامز" section="programs"     onClick={() => goDocs('programs')} />
              <DesktopLink label="مكتبة"    section="library"      onClick={() => goDocs('library')} />
              <DesktopLink label="ادمشن"    section="admission"    onClick={() => goDocs('ليه تدرس في امريكا')} />
              <DesktopLink label="نبذه عنا" section="about"        onClick={() => goHome('about')} />
              <DesktopLink label="الرئيسية" section="hero"         onClick={() => goHome('hero')} />
            </div>
          ) : (
            <button
              onClick={() => setMenuOpen(true)}
              className="flex flex-col justify-center items-start w-10 h-10 gap-[5px]"
              aria-label="القائمة"
            >
              <span className="block h-[2px] w-6 bg-white rounded-full" />
              <span className="block h-[2px] w-4 bg-white rounded-full" />
              <span className="block h-[2px] w-6 bg-white rounded-full" />
            </button>
          )}

          <Logo />
        </div>
      </nav>

      <div className="h-[72px]" />

      {/* Mobile full-screen menu */}
      <AnimatePresence>
        {isMobile && menuOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.32, ease: [0.32, 0, 0.18, 1] }}
            className="fixed inset-0 z-[200] bg-[#080808] flex flex-col overflow-hidden"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/[0.07]">
              <button
                onClick={closeMenu}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/8 hover:bg-white/14 transition-colors"
                aria-label="إغلاق"
              >
                <XMarkIcon className="h-5 w-5 text-white" />
              </button>
              <Logo />
            </div>

            <nav className="flex flex-col px-6 pt-6 gap-1 flex-1 overflow-y-auto">

              <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 }}>
                <button onClick={() => goHome('hero')}
                  className="flex justify-end items-center w-full py-4 text-[22px] font-semibold text-white border-b border-white/[0.06]">
                  الرئيسية
                </button>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
                <button onClick={() => goHome('about')}
                  className="flex justify-end items-center w-full py-4 text-[22px] font-semibold text-white border-b border-white/[0.06]">
                  نبذه عنا
                </button>
              </motion.div>

              {/* Admission expandable */}
              <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 }}>
                <button
                  onClick={() => setAdmissionOpen(!admissionOpen)}
                  className="flex justify-between items-center w-full py-4 text-[22px] font-semibold text-white border-b border-white/[0.06]"
                >
                  <motion.span
                    animate={{ rotate: admissionOpen ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                    className="text-[#A0A0A0] text-[18px]"
                  >↑</motion.span>
                  <span>ادمشن</span>
                </button>

                <AnimatePresence initial={false}>
                  {admissionOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <motion.div
                        initial={{ scaleX: 0, opacity: 0 }}
                        animate={{ scaleX: 1, opacity: 1 }}
                        transition={{ duration: 0.4, ease: 'easeOut' }}
                        style={{ transformOrigin: 'right' }}
                        className="h-[3px] bg-gradient-to-l from-[#3555C6] to-[#3E7CFF] rounded-full mb-3 mt-1"
                      />
                      <div className="flex flex-col gap-0.5 pb-2">
                        {admissionSubLinks.map((sub, i) => (
                          <motion.div key={sub.key} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}>
                            <button
                              onClick={() => goDocs(sub.key)}
                              className="flex justify-end items-center w-full py-2.5 px-3 text-[16px] text-[#B0B0B0] hover:text-white hover:bg-white/4 rounded-lg transition-colors"
                            >
                              {sub.text}
                            </button>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
                <button onClick={() => goDocs('programs')}
                  className="flex justify-end items-center w-full py-4 text-[22px] font-semibold text-white border-b border-white/[0.06]">
                  بروجرامز
                </button>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 }}>
                <button onClick={() => goDocs('programs')}
                  className="flex justify-end items-center w-full py-4 text-[22px] font-semibold text-white border-b border-white/[0.06]">
                  مسابقات
                </button>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}>
                <button onClick={() => goDocs('library')}
                  className="flex justify-end items-center w-full py-4 text-[22px] font-semibold text-white border-b border-white/[0.06]">
                  مكتبة
                </button>
              </motion.div>

            </nav>

            <div className="px-6 py-6 border-t border-white/[0.06]">
              <p className="text-[#333] text-[12px] text-right">Campus Odyssey © 2025</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default Navbar;
